import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { getImpersonationType } from '@leagueplatform/auth-utils';
import { getConfig, reportAgentPermissionError } from '@leagueplatform/core';
import { DeeplinkActionHandler } from '@leagueplatform/masonry';
import { captureAppError } from '@leagueplatform/observability';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import { MASONRY, SENTRY_CONSTANTS } from 'common/constants';
import { isAgentSession } from 'utils/agent-session';
import { matchesDeeplink } from 'utils/matches-deeplink';

type SsoImpersonationType = Exclude<
  Awaited<ReturnType<typeof getImpersonationType>>,
  false | null
>;

// Thrown when an impersonated session tries to hand off to an SSO vendor, so
// the caller shows the "Restricted action" modal instead of an error toast.
export class GeisingerSsoRestrictedError extends Error {
  constructor(readonly impersonationType: SsoImpersonationType) {
    super('SSO is not available while impersonating a member');
    this.name = 'GeisingerSsoRestrictedError';
  }
}

// The endpoint answers with a self-submitting SAML form. A browser cannot
// navigate to it with the member's token, so the form is fetched with the
// token and re-posted from the tab.
const postSamlForm = (ssoWindow: Window, html: string) => {
  const source = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector('form');
  const action = source?.getAttribute('action');
  if (!source || !action) {
    throw new Error('SSO response has no SAML form');
  }
  const form = ssoWindow.document.createElement('form');
  form.method = 'POST';
  form.action = action;
  source
    .querySelectorAll<HTMLInputElement>('input[type="hidden"]')
    .forEach(({ name, value }) => {
      const input = ssoWindow.document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
  ssoWindow.document.body.appendChild(form);
  form.submit();
};

// Long enough for a slow SSO response, short enough not to strand a blank tab.
const SSO_REQUEST_TIMEOUT_MS = 15_000;

// The tab opens synchronously, inside the click, so it isn't popup-blocked.
export const openGeisingerSso = async (url: string) => {
  const ssoWindow = window.open('', '_blank');
  if (!ssoWindow) {
    throw new Error('SSO tab was blocked');
  }
  ssoWindow.opener = null;
  const timeout = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Covers the whole hand-off, token included: aborting the fetch alone would
  // not settle a stalled getTokenSilently().
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      timeout.abort();
      reject(new Error('SSO hand-off timed out'));
    }, SSO_REQUEST_TIMEOUT_MS);
  });
  const handOff = async () => {
    // The tab is already open because the popup blocker only allows it inside
    // the click, and the token claims can only be read asynchronously. The
    // server refuses impersonated SSO anyway; this check only replaces an
    // error with the right message, before any token or request.
    const impersonationType = await getImpersonationType();
    if (impersonationType) {
      throw new GeisingerSsoRestrictedError(impersonationType);
    }
    const { legacyRestApi } = getConfig().config.core.api;
    const { search } = new URL(url, 'https://placeholder.invalid');
    const token = await StandaloneAuth.client.getTokenSilently();
    const response = await fetch(
      `${legacyRestApi}${MASONRY.DEEPLINK_GEISINGER_SSO}${search}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal: timeout.signal,
      },
    );
    if (!response.ok) {
      throw new Error(`SSO endpoint returned ${response.status}`);
    }
    return response.text();
  };
  try {
    postSamlForm(ssoWindow, await Promise.race([handOff(), deadline]));
  } catch (error) {
    ssoWindow.close();
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * The app's Masonry `deeplink` handler. Geisinger SSO links (relative
 * `sso/vendors/geisinger?target=…`, which the SDK rejects) open the League REST
 * SSO endpoint in a new tab, or show the "Restricted action" modal while
 * impersonating; everything else goes to {@link DeeplinkActionHandler}.
 * Screen-scoped overrides fall back to and restore this handler, not the SDK's.
 */
export const appDeeplinkActionHandler: typeof DeeplinkActionHandler = (
  action,
  resources,
) => {
  const { url } = action.payload;
  if (url && matchesDeeplink(url, MASONRY.DEEPLINK_GEISINGER_SSO)) {
    action.event?.preventDefault();
    // Support agents can't act as the member with SSO vendors. The flag set at
    // agent login lets most clicks skip the tab entirely.
    if (isAgentSession()) {
      reportAgentPermissionError(true, undefined, 'agent');
      return undefined;
    }
    openGeisingerSso(url).catch((error: unknown) => {
      if (error instanceof GeisingerSsoRestrictedError) {
        reportAgentPermissionError(true, undefined, error.impersonationType);
        return;
      }
      captureAppError(
        error instanceof Error ? error : new Error('Geisinger SSO failed'),
        {
          ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
          errorName: 'Geisinger SSO Failed',
        },
      );
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'STR_ERROR_STATE_BODY_API',
      });
    });
    return undefined;
  }
  return DeeplinkActionHandler(action, resources);
};
